import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  type JSX,
} from 'react';
import {
  type DehydratedRetailWarcraftTalentBuild,
  type DehydratedRetailWarcraftTalentTreeChangeSet,
  type HydratedRetailWarcraftTalentBuild,
  type LegacyRetailWarcraftTalentTreeNode,
  type RetailTalentTreeFullEntry,
  type RetailWarcraftHeroTreeBlueprint,
  type RetailWarcraftTalentTree,
  type RetailWarcraftTalentTreeBlueprint,
  type RetailWarcraftTalentTreeBuildNode,
  type RetailWarcraftTalentTreeNode,
  RetailWarcraftTalentTreeType,
} from '../typings/viewModels/retailWarcraftTalentTreeBuildNode';
import useFetch from '../hooks/useFetch';
import useGame from './useGame';

type ContextValue = Record<string, RetailWarcraftTalentTreeBlueprint>;
export const TalentTreeBlueprintContext = createContext<ContextValue>({});

export const TalentTreeBlueprintContextProvider = ({
  children,
  preloaded,
}: {
  children: ReactNode;
  preloaded: ContextValue;
}): JSX.Element => (
  <TalentTreeBlueprintContext.Provider value={preloaded}>
    {children}
  </TalentTreeBlueprintContext.Provider>
);

const useTalentTreeBlueprint = (
  changeSet?: DehydratedRetailWarcraftTalentTreeChangeSet,
): RetailWarcraftTalentTreeBlueprint | undefined => {
  const cache = useContext(TalentTreeBlueprintContext);
  const baseUrl = useGame().baseUrl;
  const [fetchState, doFetch] = useFetch<RetailWarcraftTalentTreeBlueprint>();
  const key = changeSet
    ? `${changeSet.className}_${changeSet.specName}_${changeSet.changeSetId}`
    : 'none';

  const cachedValue = cache[key];

  useEffect(() => {
    if (changeSet && !cachedValue) {
      doFetch(
        `${baseUrl}/talent-tree/blueprint/${changeSet.className}/${changeSet.specName}/${changeSet.changeSetId}`,
      );
    }
  }, [baseUrl, cachedValue, changeSet, doFetch]);

  if (!changeSet) {
    return undefined;
  }

  if (fetchState.status.isSuccess && fetchState.data) {
    if (!cachedValue) {
      cache[key] = fetchState.data;
    }

    return fetchState.data;
  }

  return cache[key];
};

/**
 * Merge a blueprint with a dehydrated build to get a full entry.
 *
 * This is not intended to be used directly; it is only exported so that tests can run without spinning up a full React context.
 */
export function mergeBlueprintWithSelectedNodes(
  iconClassName: string,
  backgroundUrl: string,
  heroTree: RetailWarcraftHeroTreeBlueprint | null,
  nodesByEntryId: Record<number, RetailWarcraftTalentTreeNode>,
  dehydratedBuild: DehydratedRetailWarcraftTalentBuild,
  includeUnselected: boolean,
  isHeatmap: boolean,
): RetailTalentTreeFullEntry {
  const tree: RetailWarcraftTalentTree = [];
  const hydratedMap: Record<number, RetailWarcraftTalentTreeBuildNode> = {};

  for (const [entryId, points] of dehydratedBuild.selectedNodes) {
    const node = nodesByEntryId[entryId];
    if (!node) {
      continue;
    }

    if (isHeatmap) {
      let existing = hydratedMap[node.nodeId];
      if (!existing) {
        existing = {
          node,
          pointsInvested: 0,
          heatMapPercent: {
            values: node.abilities.map(() => 0),
          },
        };
        hydratedMap[node.nodeId] = existing;
      }

      const entryIndex = node.abilities.findIndex(
        (entry) => entry.id === entryId,
      );
      if (existing.heatMapPercent) {
        existing.heatMapPercent.values[entryIndex] = points ?? 0.0;
      }
    } else {
      const existing = hydratedMap[node.nodeId];
      hydratedMap[node.nodeId] = {
        node,
        entry: node.abilities.find((entry) => entry.id === entryId),
        pointsInvested: (existing?.pointsInvested ?? 0) + (points ?? 1),
      };
    }
  }

  if (includeUnselected) {
    for (const node of Object.values(nodesByEntryId)) {
      if (!hydratedMap[node.nodeId]) {
        hydratedMap[node.nodeId] = {
          node,
          pointsInvested: 0,
          heatMapPercent: isHeatmap
            ? {
                values: node.abilities.map(() => 0),
              }
            : undefined,
        };
      }
    }
  }

  for (const buildNode of Object.values(hydratedMap)) {
    let row = tree[buildNode.node.row];
    if (!row) {
      row = [];
      tree[buildNode.node.row] = row;
    }

    if (isHeatmap) {
      let maxPct = 0;
      let entry = undefined;
      for (let i = 0; i < buildNode.node.abilities.length; i++) {
        const pct = buildNode.heatMapPercent?.values[i] ?? 0;
        if (pct > maxPct) {
          entry = buildNode.node.abilities[i];
          maxPct = pct;
        }
      }

      buildNode.entry = entry;
    }

    row.push(buildNode);
  }

  return {
    iconClassName,
    artwork: backgroundUrl,
    tree,
    heroTree,
  };
}

export function normalizePossibleOldNodes(
  nodes: (RetailWarcraftTalentTreeNode | LegacyRetailWarcraftTalentTreeNode)[],
): RetailWarcraftTalentTreeNode[] {
  return 'treeType' in nodes[0]
    ? (nodes as RetailWarcraftTalentTreeNode[])
    : nodes.map<RetailWarcraftTalentTreeNode>((node) => {
        if ('treeType' in node) {
          return node;
        }

        return {
          ...node,
          treeType: node.isClassTreeNode
            ? RetailWarcraftTalentTreeType.ClassTree
            : RetailWarcraftTalentTreeType.SpecTree,
        };
      });
}

const useBlueprintEntryMaps = (blueprint?: RetailWarcraftTalentTreeBlueprint) =>
  useMemo(() => {
    if (!blueprint) {
      return {};
    }

    const treesByType: Record<
      RetailWarcraftTalentTreeType,
      Record<number, RetailWarcraftTalentTreeNode>
    > = {
      [RetailWarcraftTalentTreeType.ClassTree]: {},
      [RetailWarcraftTalentTreeType.SpecTree]: {},
      [RetailWarcraftTalentTreeType.HeroTree]: {},
    };

    const allNodes = normalizePossibleOldNodes(blueprint.changeSet.allNodes);

    for (const node of allNodes) {
      const map = treesByType[node.treeType];

      for (const entry of node.abilities) {
        map[entry.id] = node;
      }
    }

    return {
      classTree: treesByType[RetailWarcraftTalentTreeType.ClassTree],
      specTree: treesByType[RetailWarcraftTalentTreeType.SpecTree],
      heroTree: treesByType[RetailWarcraftTalentTreeType.HeroTree],
    };
  }, [blueprint]);

export const useHydratedTalentTreeBuild = (
  dehydratedBuild: DehydratedRetailWarcraftTalentBuild,
  includeUnselected = true,
  isHeatmap = false,
): HydratedRetailWarcraftTalentBuild | undefined => {
  const blueprint = useTalentTreeBlueprint(dehydratedBuild.changeSet);
  const { classTree, specTree, heroTree } = useBlueprintEntryMaps(blueprint);
  return useMemo(() => {
    if (!blueprint || !classTree || !specTree || !heroTree) {
      // these are undefined in error cases. in normal operation, they are simply empty if the user does not have any of the tree nodes selected.
      return undefined;
    }

    const heroTreeData = dehydratedBuild.heroSpecId
      ? blueprint.heroTrees?.find(
          (tree) => tree.id === dehydratedBuild.heroSpecId,
        )
      : null;

    return {
      spellModifierId: blueprint.spellModifierId,
      classTree: mergeBlueprintWithSelectedNodes(
        blueprint.classIcon,
        blueprint.classBackgroundUrl,
        null,
        classTree,
        dehydratedBuild,
        includeUnselected,
        isHeatmap,
      ),
      specTree: mergeBlueprintWithSelectedNodes(
        blueprint.specIcon,
        blueprint.specBackgroundUrl,
        null,
        specTree,
        dehydratedBuild,
        includeUnselected,
        isHeatmap,
      ),
      heroTree: mergeBlueprintWithSelectedNodes(
        '',
        '',
        heroTreeData ?? null,
        heroTree,
        dehydratedBuild,
        includeUnselected,
        isHeatmap,
      ),
    };
  }, [
    dehydratedBuild,
    includeUnselected,
    isHeatmap,
    classTree,
    specTree,
    heroTree,
    blueprint,
  ]);
};

export default useTalentTreeBlueprint;
